import moment from "moment";
import React from "react";
import type { Chart, ScatterDataPoint } from "chart.js";
import { Scatter } from "react-chartjs-2";
import { loadChartData } from "../services/api";
import { MILLISECONDS_PER_WEEK } from "../config/constants";
import { convertLegacyData } from "../converters/chartData";
import { convertLegacyOptions } from "../converters/chartOptions";
import { checkRangeLimits, getRangeLimitsFromScaling } from "../utils/range";
import type { ChartDataDto, LegacyLineDataset, LegacyChartOptions } from "../types";
import { ensureArray, getValueOrDefault, makeId } from "../utils/common";
import ChartFullscreenWrapper from "./ChartFullscreenWrapper";
import Loader from "./Loader";
import RangeInput, { type RangeInputRef } from "./RangeInput";

type ScalingState = {
  horizontalAxisMin: number;
  horizontalAxisMax: number;
  verticalAxisMin: number;
  verticalAxisMax: number;
  minXloaded: number;
  maxXloaded: number;
};

type Props = {
  chartName: string;
  options: LegacyChartOptions;
  data: ChartDataDto;
  height?: number;
  legendDivId?: string;
  yParameterTypeIds?: number[];
  profileId?: number;
  useLiveDataAccessor?: boolean;
  isLongitudinalProfileChart?: boolean;
  registerRangeReference?: (instance: RangeInputRef) => void;
  registerChartReference?: (instance: MeasurementChart) => void;
  onPlotVisibilityChanged?: (
    event: unknown,
    legendItem: { text?: string; datasetIndex?: number },
    legend: unknown
  ) => void;
  onRangeChanged: (scaling: ScalingState) => void;
  getHiddenPointIds?: () => number[];
  getHiddenDatasets?: () => string[];
};

type ComponentState = {
  dataIsLoading: boolean;
};

type MutableScaleOptions = {
  min?: number | string;
  max?: number | string;
  [key: string]: unknown;
};

type MeasurementChartInstance = Chart<"scatter", ScatterDataPoint[]>;
type JQueryEventTarget = {
  on: (eventName: string, handler: () => void) => void;
  off: (eventName: string, handler: () => void) => void;
};
type JQueryFactory = (target: Document) => JQueryEventTarget;

export default class MeasurementChart extends React.Component<Props, ComponentState> {
  state: ComponentState = {
    dataIsLoading: false
  };

  chartRef = React.createRef<MeasurementChartInstance>();

  containerRef = React.createRef<HTMLDivElement>();

  resizeObserver: ResizeObserver | null = null;

  resizeTimer = 0;

  refName = makeId();

  scaling: ScalingState = {
    horizontalAxisMin: Number.NaN,
    horizontalAxisMax: Number.NaN,
    verticalAxisMin: Number.NaN,
    verticalAxisMax: Number.NaN,
    minXloaded: Number.NaN,
    maxXloaded: Number.NaN
  };

  verticalRange: RangeInputRef | null = null;

  horizontalRange: RangeInputRef | null = null;

  componentDidMount(): void {
    this.props.registerChartReference?.(this);
    window.addEventListener("resize", this.refreshChartSize);
    document.addEventListener("shown.bs.tab", this.refreshChartSize);
    (window as unknown as { $?: JQueryFactory }).$?.(document).on("shown.bs.tab", this.refreshChartSize);
    const observedContainer = this.containerRef.current?.parentElement ?? this.containerRef.current;
    if (window.ResizeObserver && observedContainer) {
      this.resizeObserver = new ResizeObserver(this.refreshChartSize);
      this.resizeObserver.observe(observedContainer);
    }

    if (this.props.legendDivId) {
      this.renderLegend(this.props.legendDivId);
    }

    this.setPrintUrl();
    this.refreshChartSize();
  }

  componentDidUpdate(): void {
    this.setPrintUrl();
    this.refreshChartSize();
  }

  componentWillUnmount(): void {
    window.clearTimeout(this.resizeTimer);
    this.resizeObserver?.disconnect();
    window.removeEventListener("resize", this.refreshChartSize);
    document.removeEventListener("shown.bs.tab", this.refreshChartSize);
    (window as unknown as { $?: JQueryFactory }).$?.(document).off("shown.bs.tab", this.refreshChartSize);
  }

  getChartInstance(): MeasurementChartInstance | null {
    return this.chartRef.current;
  }

  refreshChartSize = (): void => {
    const chart = this.getChartInstance();
    if (!chart) {
      return;
    }

    window.requestAnimationFrame(() => {
      chart.resize();
      chart.update("none");
    });
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      chart.resize();
      chart.update("none");
    }, 150);
  };

  getScale(axis: "x" | "y"): MutableScaleOptions | null {
    const chart = this.getChartInstance();
    if (!chart) {
      return null;
    }

    const id = this.getAxisId(axis);
    const scales = (chart.options.scales ?? {}) as unknown as Record<string, MutableScaleOptions>;
    return scales[id] ?? null;
  }

  getAxisId(axis: "x" | "y"): string {
    const axes = axis === "x" ? this.props.options.scales?.xAxes : this.props.options.scales?.yAxes;
    return axes?.[0]?.id ?? `${axis}-axis-1`;
  }

  registerVerticalRange = (instance: RangeInputRef): void => {
    this.verticalRange = instance;
  };

  registerHorizontalRange = (instance: RangeInputRef): void => {
    this.horizontalRange = instance;
    this.props.registerRangeReference?.(instance);
  };

  notifyScalingChanged = (): void => {
    this.props.onRangeChanged(this.scaling);
  };

  getAxisType(xAxis: boolean): string {
    const controls = this.props.options.scalingControls;
    return (
      (xAxis ? controls?.xAxisScalingControl : controls?.yAxisScalingControl)?.scalingProperty ??
      (xAxis ? "time" : "ticks")
    );
  }

  getAxisScaling(getScale: (xScale: MutableScaleOptions, yScale: MutableScaleOptions) => number | undefined): number {
    const xScale = this.getScale("x") ?? {};
    const yScale = this.getScale("y") ?? {};
    return getScale(xScale, yScale) ?? 0;
  }

  updateExportScalingUrl(): void {
    const exportDetailLink = document.querySelector<HTMLAnchorElement>("a#exportDetail");
    const exportLink = document.querySelector<HTMLButtonElement>("button#export");

    if (!exportDetailLink) {
      return;
    }

    const exportDetailUrl = exportDetailLink.getAttribute("href") ?? "";
    const title = String(this.props.options.title?.text ?? this.props.chartName);
    const urlParams = new URLSearchParams(new URL(window.location.origin + exportDetailUrl).search);

    urlParams.set(`scaling_${title}`, JSON.stringify(this.scaling));

    const rangeControls = document.querySelector("div.range-controls");
    const rangeInputs = rangeControls?.querySelectorAll("input") ?? [];

    urlParams.set(
      "excavationScaling",
      JSON.stringify({
        start: rangeInputs.length >= 2 ? (rangeInputs[0] as HTMLInputElement).value : "",
        end: rangeInputs.length >= 2 ? (rangeInputs[1] as HTMLInputElement).value : ""
      })
    );

    const nextHref = `${exportDetailUrl.split("?")[0]}?${decodeURIComponent(urlParams.toString())}`;
    exportDetailLink.setAttribute("href", nextHref);
    if (exportLink) {
      exportLink.setAttribute("href", nextHref);
    }
  }

  changeScaling(changeScale: (xScale: MutableScaleOptions, yScale: MutableScaleOptions) => void): void {
    const chart = this.getChartInstance();
    if (!chart) {
      return;
    }

    const xScale = this.getScale("x") ?? {};
    const yScale = this.getScale("y") ?? {};

    changeScale(xScale, yScale);

    this.updateExportScalingUrl();
    chart.update();
  }

  mapPointData(point: { metadata?: Record<string, unknown>; pointId?: number; measurementId?: number; x: string | number; y: number }): {
    metadata: Record<string, unknown>;
    pointId?: number;
    measurementId?: number;
    x: string | number;
    y: number;
  } {
    return {
      metadata: point.metadata ?? {},
      pointId: point.pointId,
      measurementId: point.measurementId,
      x: point.x,
      y: point.y
    };
  }

  isWarningDataset(dataset: LegacyLineDataset): boolean {
    return (
      (dataset.measurementTypeId === undefined || dataset.measurementTypeId === 0) &&
      ensureArray(dataset.data).length === 2
    );
  }

  adjustWarningDatasetsLimits(existingDatasets: LegacyLineDataset[], newDataset: LegacyLineDataset, prepend: boolean): void {
    existingDatasets.forEach((existingDataset) => {
      if (!this.isWarningDataset(existingDataset)) {
        return;
      }

      const target = newDataset.data[prepend ? 0 : 1];
      if (!target || target.x == null) {
        return;
      }

      if (!existingDataset.data[prepend ? 0 : 1]) {
        return;
      }

      existingDataset.data[prepend ? 0 : 1].x = target.x;
    });
  }

  loadNextData(data: LegacyLineDataset[], prepend: boolean): void {
    const chart = this.getChartInstance();
    if (!chart) {
      return;
    }

    const datasets = chart.data.datasets as unknown as LegacyLineDataset[];
    const rangeLimits = getRangeLimitsFromScaling(this.scaling, 1, true);

    data.forEach((newDataset) => {
      if (this.isWarningDataset(newDataset)) {
        this.adjustWarningDatasetsLimits(datasets, newDataset, prepend);
        return;
      }

      datasets.forEach((existingDataset) => {
        if (this.isWarningDataset(existingDataset)) {
          return;
        }

        if (existingDataset.pointId !== newDataset.pointId) {
          return;
        }

        if (existingDataset.measurementTypeId !== newDataset.measurementTypeId) {
          return;
        }

        const incoming = ensureArray(newDataset.data);
        const existing = ensureArray(existingDataset.data);

        const unique = incoming.filter(
          (point) => existing.findIndex((p) => p.measurementId === point.measurementId) === -1
        );

        const points = prepend ? [...unique].reverse() : unique;
        points.forEach((point) => {
          if (prepend) {
            existingDataset.data.unshift(this.mapPointData(point));
          } else {
            existingDataset.data.push(this.mapPointData(point));
          }
          checkRangeLimits(rangeLimits, point.y);
        });
      });
    });

    this.scaling.verticalAxisMax = rangeLimits.max + 1;
    this.scaling.verticalAxisMin = rangeLimits.min - 1;

    this.changeScaling((_xScale, yScale) => {
      yScale.max = this.scaling.verticalAxisMax;
      yScale.min = this.scaling.verticalAxisMin;
    });

    this.verticalRange?.setState({
      currentMax: this.scaling.verticalAxisMax,
      max: this.scaling.verticalAxisMax,
      currentMin: this.scaling.verticalAxisMin,
      min: this.scaling.verticalAxisMin
    });
  }

  requestNextData(fromMoment: moment.Moment, toMoment: moment.Moment, prepend: boolean): void {
    this.setState({ dataIsLoading: true });

    const currentDatasets = this.props.data.datasets ?? this.props.data.dataSets ?? [];
    const pointIds = currentDatasets
      .filter((item) => item.measurementTypeId !== undefined && item.measurementTypeId !== 0)
      .map((item) => item.pointId)
      .filter((item): item is number => typeof item === "number")
      .join(",");

    const parameterIds = ensureArray(this.props.yParameterTypeIds).join(",");

    loadChartData(
      pointIds,
      fromMoment.format("YYYY-MM-DD"),
      toMoment.format("YYYY-MM-DD"),
      parameterIds,
      this.props.useLiveDataAccessor ?? false
    )
      .then((result) => {
        this.loadNextData(result, prepend);
      })
      .finally(() => {
        this.setState({ dataIsLoading: false });
      });
  }

  onParentDateRangeChanged = (minVal: number, maxVal: number): void => {
    if (this.getAxisType(true) !== "time") {
      return;
    }

    if (maxVal !== this.scaling.horizontalAxisMax) {
      const newVal = moment(maxVal).startOf("day").valueOf();
      this.scaling.horizontalAxisMax = newVal;
      if (newVal > this.scaling.maxXloaded) {
        this.requestNextData(moment(this.scaling.maxXloaded), moment(newVal), false);
        this.scaling.maxXloaded = newVal;
      }

      this.changeScaling((xScale) => {
        xScale.max = newVal;
      });
    }

    if (minVal !== this.scaling.horizontalAxisMin) {
      const newVal = moment(minVal).startOf("day").valueOf();
      this.scaling.horizontalAxisMin = newVal;
      if (newVal < this.scaling.minXloaded) {
        this.requestNextData(moment(newVal), moment(this.scaling.minXloaded), true);
        this.scaling.minXloaded = newVal;
      }

      this.changeScaling((xScale) => {
        xScale.min = newVal;
      });
    }
  };

  handleHorizontalMaxChanged = (value: number): void => {
    if (this.getAxisType(true) === "time") {
      const newVal = moment(value).startOf("day").valueOf();
      if (newVal > this.scaling.maxXloaded) {
        return;
      }

      this.scaling.horizontalAxisMax = newVal;
      this.changeScaling((xScale) => {
        xScale.max = newVal;
      });
      return;
    }

    this.scaling.horizontalAxisMax = value;
    this.changeScaling((xScale) => {
      xScale.max = value;
    });
  };

  handleHorizontalMinChanged = (value: number): void => {
    if (this.getAxisType(true) === "time") {
      const newVal = moment(value).startOf("day").valueOf();
      if (newVal < this.scaling.minXloaded) {
        return;
      }

      this.scaling.horizontalAxisMin = newVal;
      this.changeScaling((xScale) => {
        xScale.min = newVal;
      });
      return;
    }

    this.scaling.horizontalAxisMin = value;
    this.changeScaling((xScale) => {
      xScale.min = value;
    });
  };

  handleVerticalMaxChanged = (value: number): void => {
    const actual = this.getAxisScaling((_xScale, yScale) => yScale.max as number | undefined);
    if (actual === value) {
      return;
    }

    this.scaling.verticalAxisMax = value;
    this.changeScaling((_xScale, yScale) => {
      yScale.max = value;
    });
  };

  handleVerticalMinChanged = (value: number): void => {
    const actual = this.getAxisScaling((_xScale, yScale) => yScale.min as number | undefined);
    if (actual === value) {
      return;
    }

    this.scaling.verticalAxisMin = value;
    this.changeScaling((_xScale, yScale) => {
      yScale.min = value;
    });
  };

  setPrintUrl(): void {
    const exportDetailLink = document.querySelector<HTMLAnchorElement>("a#exportDetail");
    if (!exportDetailLink) {
      return;
    }

    const exportDetailUrl = (exportDetailLink.getAttribute("href") ?? "").split("?")[0];
    const urlParams = new URLSearchParams(new URL(window.location.origin + exportDetailUrl).search);

    const hiddenPointIds = this.props.getHiddenPointIds?.() ?? [];
    const hiddenDatasets = this.props.getHiddenDatasets?.() ?? [];

    urlParams.set("excludedPointIds", hiddenPointIds.join());
    urlParams.set("excludedDatasets", hiddenDatasets.join());

    const exportDetailQuery = (exportDetailLink.getAttribute("href") ?? "").split("?")[1];

    if (exportDetailQuery) {
      const preserved = exportDetailQuery
        .split("&")
        .filter((item) => !item.includes("excludedPointIds") && !item.includes("excludedDatasets"));

      exportDetailLink.setAttribute(
        "href",
        `${exportDetailUrl}?${urlParams.toString()}${preserved.length > 0 ? `&${preserved.join("&")}` : ""}`
      );
    } else {
      exportDetailLink.setAttribute("href", `${exportDetailUrl}?${urlParams.toString()}`);
    }
  }

  renderLegend(legendDivId: string): void {
    const chart = this.getChartInstance();
    if (!chart) {
      return;
    }

    const container = document.getElementById(legendDivId);
    if (!container) {
      return;
    }

    const labels = (chart.data.datasets as LegacyLineDataset[])
      .map((dataset) => {
        const color = dataset.backgroundColor ?? "#333";
        return `<li style=\"list-style:none;padding:6px\"><div class=\"legendValue\"><span style=\"background-color:${color}\">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span><span style=\"padding:6px\" class=\"label\">${dataset.label}</span></div></li>`;
      })
      .join("");

    container.innerHTML = `<ul style=\"display:flex;flex-flow:row wrap;\">${labels}</ul>${container.innerHTML}`;
  }

  renderScalingControls() {
    const options = this.props.options;
    const data = this.props.data;

    const xAxisScalingControl = options.scalingControls?.xAxisScalingControl;
    const yAxisScalingControl = options.scalingControls?.yAxisScalingControl;

    const xAxes = options.scales?.xAxes ?? [];
    const yAxes = options.scales?.yAxes ?? [];

    const scalingSettings = data.scalingSettings ?? {};

    const dataXAxisMin = getValueOrDefault(scalingSettings.dataXAxisMin, -10);
    const dataXAxisMax = getValueOrDefault(scalingSettings.dataXAxisMax, 10);
    const dataXAxisMinPos = getValueOrDefault(scalingSettings.dataXAxisMinPos, -10);
    const dataXAxisMaxPos = getValueOrDefault(scalingSettings.dataXAxisMaxPos, 10);
    const dataYAxisMin = getValueOrDefault(scalingSettings.dataYAxisMin, -10);
    const dataYAxisMax = getValueOrDefault(scalingSettings.dataYAxisMax, 10);

    const lastXAxisMin = scalingSettings.lastXAxisMin;
    const lastXAxisMax = scalingSettings.lastXAxisMax;
    const lastYAxisMin = scalingSettings.lastYAxisMin;
    const lastYAxisMax = scalingSettings.lastYAxisMax;

    const xIsDate = xAxes.some((x) => x.type === "time");
    const yIsDate = yAxes.some((y) => y.type === "time");

    const normalizeAxisValue = (value: unknown, isDate: boolean): number => {
      return isDate ? moment(value as moment.MomentInput).startOf("day").valueOf() : Number(value);
    };

    if (!Number.isFinite(this.scaling.horizontalAxisMin) || !Number.isFinite(this.scaling.horizontalAxisMax)) {
      this.scaling = {
        horizontalAxisMin: normalizeAxisValue(lastXAxisMin ?? dataXAxisMin, xIsDate),
        horizontalAxisMax: normalizeAxisValue(lastXAxisMax ?? dataXAxisMax, xIsDate),
        verticalAxisMin: Number(lastYAxisMin ?? dataYAxisMin),
        verticalAxisMax: Number(lastYAxisMax ?? dataYAxisMax),
        minXloaded: moment(lastXAxisMin ?? dataXAxisMinPos).startOf("day").valueOf(),
        maxXloaded: moment(lastXAxisMax ?? dataXAxisMaxPos).startOf("day").valueOf()
      };
    }

    return (
      <div className="scaling-controls">
        {xAxisScalingControl?.display ? (
          <RangeInput
            min={dataXAxisMin}
            max={dataXAxisMax}
            values={[
              (lastXAxisMin ?? dataXAxisMinPos) as number | string,
              (lastXAxisMax ?? dataXAxisMaxPos) as number | string
            ]}
            step={xAxisScalingControl.stepSize ?? MILLISECONDS_PER_WEEK}
            onMaxChanged={this.handleHorizontalMaxChanged}
            onMinChanged={this.handleHorizontalMinChanged}
            onParentDateRangeChanged={this.onParentDateRangeChanged}
            onAfterChange={this.notifyScalingChanged}
            valueIsDate={xIsDate}
            useDataMax={xAxisScalingControl.useAlwaysDataMax}
            registerRangeReference={this.registerHorizontalRange}
            label={xAxisScalingControl.label ?? "Axis X"}
          />
        ) : null}

        {yAxisScalingControl?.display ? (
          <RangeInput
            min={Number(dataYAxisMin)}
            max={Number(dataYAxisMax)}
            values={[
              Number(lastYAxisMin ?? dataYAxisMin),
              Number(lastYAxisMax ?? dataYAxisMax)
            ]}
            step={yAxisScalingControl.stepSize ?? 1}
            onMaxChanged={this.handleVerticalMaxChanged}
            onMinChanged={this.handleVerticalMinChanged}
            onAfterChange={this.notifyScalingChanged}
            valueIsDate={yIsDate}
            useDataMax={yAxisScalingControl.useAlwaysDataMax}
            label={yAxisScalingControl.label ?? "Axis Y"}
            registerRangeReference={this.registerVerticalRange}
          />
        ) : null}
      </div>
    );
  }

  buildLegendHandler(): (
    event: unknown,
    item: { text?: string; datasetIndex?: number },
    legend: unknown
  ) => void {
    return (event, legendItem, legend) => {
      this.props.onPlotVisibilityChanged?.(event, legendItem, legend);
      this.setPrintUrl();
    };
  }

  buildTooltipCallbacks(): Record<string, unknown> | undefined {
    const tooltipOptions = this.props.options.tooltips;
    if (!tooltipOptions?.customText) {
      return undefined;
    }

    return {
      label: (context: { dataset: { data: Array<{ metadata?: Record<string, unknown> }> }; dataIndex: number; parsed: { x: number; y: number } }) => {
        const point = context.dataset.data[context.dataIndex];
        const text = point?.metadata?.tooltipText;
        return typeof text === "string" ? text : `${context.parsed.y}, ${context.parsed.x}`;
      }
    };
  }

  render() {
    const legacyOptions = {
      ...(this.props.options ?? {})
    } as LegacyChartOptions;

    legacyOptions.tooltips = {
      ...(legacyOptions.tooltips ?? {}),
      callbacks: this.buildTooltipCallbacks()
    };

    const convertedOptions = convertLegacyOptions(
      legacyOptions,
      this.props.onPlotVisibilityChanged ? this.buildLegendHandler() : undefined
    );

    const datasets = this.props.data.datasets ?? this.props.data.dataSets ?? [];

    return (
      <div className="MeasurementChart" ref={this.containerRef}>
        <ChartFullscreenWrapper>
          {this.state.dataIsLoading ? (
            <div className="chart-loader-overlay">
              <Loader />
            </div>
          ) : null}

          <Scatter
            ref={this.chartRef}
            options={convertedOptions}
            data={convertLegacyData(datasets)}
            height={this.props.height}
          />

          {this.props.options && this.props.data ? this.renderScalingControls() : null}
        </ChartFullscreenWrapper>
      </div>
    );
  }
}
